import { Box, Paper, Switch, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { useMemo, useState } from 'react';
import MonitorIcon from '@mui/icons-material/Monitor';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { ThemeProvider } from '@mui/material/styles';
import { sharedStyles } from '../../utils/Style';
import { SlidingBanners } from './components/SlidingBanners';
import { HomepageEditorContextProvider } from './HomepageEditorContext';
import { AppHeader } from './components/AppHeader';
import { SliderCarousel } from './components/SliderCarousel';
import { StyleEditorProvider } from './StyleEditor/StyleEditorProvider';
import { frontendSettingsAtom } from '../../atoms/FrontendSettings';
import { ProductGrid } from './components/ProductGrid';
import { DailyProducts } from './components/DailyProducts';
import { Banners } from './components/Banners';
import { Brands } from './components/Brands';
import { Blogs } from './components/Blogs';
import { createPreviewTheme } from './utils/createPreviewTheme';
import { FooterMobile } from './components/FooterMobile';
import { FooterDesktop } from './components/FooterDesktop';

export const HomepageEditorPage = () => {
  const settings = useRecoilValue(frontendSettingsAtom);

  const [isMobile, setIsMobile] = useState(false);

  const previewTheme = useMemo(
    () => createPreviewTheme(isMobile ? 450 : window.innerWidth),
    [isMobile],
  );

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Typography variant='h5' sx={{ ...sharedStyles.pageTitle, my: 'auto' }}>
          Ana Sayfa Düzenleyici
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            ml: 'auto',
          }}
        >
          <MonitorIcon color={isMobile ? 'inherit' : 'primary'} />

          <Switch
            checked={isMobile}
            onChange={() => setIsMobile((prev) => !prev)}
          />

          <PhoneAndroidIcon color={isMobile ? 'primary' : 'inherit'} />
        </Box>
      </Box>

      <ThemeProvider theme={previewTheme}>
        <HomepageEditorContextProvider isMobile={isMobile}>
          <StyleEditorProvider>
            <Paper
              sx={{
                width: isMobile ? 450 : '100%',
                flex: '1 1 0',
                overflowY: 'auto',
                minHeight: 0,
              }}
            >
              <SlidingBanners />

              <AppHeader />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                {settings.settingsV1.slider_status === '1' && (
                  <SliderCarousel />
                )}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    gap: 4.5,
                    alignItems: 'center',
                    boxSizing: 'border-box',
                    px: isMobile ? 1.5 : 5,
                  }}
                >
                  {settings.settingsV1.popular_products_status === '1' && (
                    <ProductGrid
                      title={settings.settingsV1.popular_products_text}
                      popularity='popular'
                      titleStyle={
                        settings.settingsV2?.popularProductsSettings?.titleStyle
                      }
                      dividerStyle={
                        settings.settingsV2?.popularProductsSettings
                          ?.dividerStyle
                      }
                    />
                  )}

                  {settings.settingsV1.hightlights_status === '1' && (
                    <ProductGrid
                      title={settings.settingsV1.highlight_products}
                      popularity='highlighted'
                      titleStyle={
                        settings.settingsV2?.highlightedProductsSettings
                          ?.titleStyle
                      }
                      dividerStyle={
                        settings.settingsV2?.highlightedProductsSettings
                          ?.dividerStyle
                      }
                    />
                  )}

                  {settings.settingsV1.daily_products_status === '1' && (
                    <DailyProducts />
                  )}

                  {settings.settingsV1.banner_status === '1' && <Banners />}

                  {settings.settingsV1.brands_status === '1' && <Brands />}
                </Box>

                {settings.settingsV1.blogs_status === '1' && <Blogs />}
              </Box>

              {isMobile ? <FooterMobile /> : <FooterDesktop />}
            </Paper>
          </StyleEditorProvider>
        </HomepageEditorContextProvider>
      </ThemeProvider>
    </Box>
  );
};
